import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { GetApp as GetAppIcon } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid';

import { AppDataGridColumnSettingsButton, JobIdAutocomplete, JobNameAutocomplete, JobNameWithScheduleLabel, OrderIdAutocomplete, Page, ReportTable } from 'components';
import { useSession } from 'store';
import { t } from 'i18n';
import { callApi, download, formatFieldName } from 'utils';
import { allTrackers } from 'consts';

const defaultFilter = {idtype: 'parent', id: '', orderId: '', jobId: '', q: '', start_date: null, end_date: null};
const defaultLimit = 100;
const idColumn = { minWidth: 64, flex: 0.45 };
const metricColumn = { minWidth: 76, flex: 0.55 };
const rateColumn = { minWidth: 84, flex: 0.6 };
const completionRateColumn = { ...rateColumn, numeric: true };
const trackerColumnNames = allTrackers.map((_, index) => `tracker_${index}`);
const trackerColumns = Object.fromEntries(allTrackers.map((label, trackerIndex) => [
  trackerColumnNames[trackerIndex],
  { label, trackerIndex, minWidth: 148, flex: 1.15 },
]));
const columns = {
  date: { minWidth: 104, flex: 0.75 },
  parent: idColumn,
  job: idColumn,
  name: { minWidth: 220, flex: 3, truncate: true },
  region: { minWidth: 160, flex: 1.5, truncate: true },
  task_status: { minWidth: 96, flex: 0.75 },
  ad_type: { minWidth: 76, flex: 0.55 },
  daypart: { minWidth: 108, flex: 0.85 },
  expected_overrun: { ...rateColumn, numeric: true },
  scheduled_imp: { ...metricColumn, numeric: true },
  scheduled_clk: { ...metricColumn, numeric: true },
  imps: metricColumn,
  imp_completion_rate: completionRateColumn,
  clicks: metricColumn,
  clk_completion_rate: completionRateColumn,
  ctr: metricColumn,
  ad_slot_name: { minWidth: 100, flex: 0.8, truncate: true },
  start_time: { minWidth: 88, flex: 0.6 },
  end_time: { minWidth: 88, flex: 0.6 },
  scheduled: { ...metricColumn, numeric: true },
  actual: { ...metricColumn, numeric: true },
  completion_rate: completionRateColumn,
  ...trackerColumns,
};
const getDefaultDateRange = () => {
  const endDate = dayjs();
  return {
    start_date: endDate.subtract(6, 'day'),
    end_date: endDate,
  };
};
const getDefaultFilter = () => ({
  ...defaultFilter,
  ...getDefaultDateRange(),
});
const getReportFormat = (params) => params.get('format') === 'detail' ? 'detail' : 'split';
const buildReportParams = (searchParams, {pagination=true, region=false} = {}) => {
  const params = new URLSearchParams(searchParams);
  region ? params.delete('format') : params.set('format', getReportFormat(params));
  const defaultDateRange = getDefaultDateRange();

  params.get('start') || params.set('start', defaultDateRange.start_date.format('YYYY-MM-DD'));
  params.get('end') || params.set('end', defaultDateRange.end_date.format('YYYY-MM-DD'));

  if (pagination) {
    params.set('limit', defaultLimit);
  } else {
    params.delete('page');
    params.delete('limit');
  }

  return params;
};
const sanitizeDownloadName = (name) => String(name || '')
  .trim()
  .replace(/[\\/:*?"<>|]+/g, '-')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .slice(0, 80);
const getReportDownloadBaseName = async (params, title) => {
  if (params.get('idtype') !== 'parent') return title;

  const id = Number(params.get('id'));
  if (id <= 0) return title;

  try {
    const lookupParams = new URLSearchParams({
      gid: String(id),
      q: `id:${id}`,
      limit: '1',
    });
    const token = localStorage.getItem('token');
    const resp = await fetch(`/api/jobs?${lookupParams.toString()}`, {
      headers: {
        Accept: 'application/json',
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
      },
    });
    if (!resp.ok) throw new Error(`failed to load order ${id}`);

    const res = await resp.json();
    const name = res.rows?.[0]?.name?.replace(/ 1$/, '');
    const suffix = sanitizeDownloadName([name, id].filter(Boolean).join('-'));
    return suffix ? `${title}-${suffix}` : `${title}-${id}`;
  } catch (ex) {
    console.error(ex);
    return `${title}-${id}`;
  }
};
const filterItemSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
};
const filterToolbarSx = {
  width: '100%',
  flexWrap: 'wrap',
};
const textFilterItemSx = {
  ...filterItemSx,
  flex: {xs: '1 1 100%', sm: '1 1 210px', md: '1 1 170px'},
  minWidth: {xs: '100%', sm: 160},
};
const dateFilterItemSx = {
  ...filterItemSx,
  flex: {xs: '1 1 100%', sm: '1.2 1 300px'},
  minWidth: {xs: '100%', sm: 300},
};
const filterActionsSx = {
  display: 'flex',
  flex: {xs: '1 1 100%', sm: '0 0 auto'},
  gap: 1,
  whiteSpace: 'nowrap',
};
const filterControlSx = {
  p: 0,
  flex: '1 1 130px',
  minWidth: 0,
};
const datePickerSx = {
  flex: '1 1 120px',
  minWidth: 0,
  '& .MuiInputBase-input': {p: '6px'},
};
const filterButtonSx = {
  minWidth: 88,
  flexShrink: 0,
};

function formatTrackerCell(row, trackerIndex) {
  const count = Number(row.tracker_cnt?.[trackerIndex]) || 0;
  if (!count) return '--';

  const landing = Number(row.landing) || 0;
  const stay = Number(row.tracker_stay?.[trackerIndex]);
  const bounce = Number(row.tracker_bounce?.[trackerIndex]) || 0;
  return [
    landing ? `${(100 * count / landing).toFixed(1)}%` : '--',
    Number.isFinite(stay) ? `${Math.ceil(stay)}s` : '--',
    bounce ? `${(100 * bounce / count).toFixed(1)}%` : '--',
  ].join(' / ');
}

function renderCellValue(col, row) {
  const trackerIndex = columns[col]?.trackerIndex;
  if (Number.isInteger(trackerIndex)) return formatTrackerCell(row, trackerIndex);
  return col === 'name' ? <JobNameWithScheduleLabel job={row} name={row[col]} compact /> : row[col];
}

const JobReports = ({reportType, embedded, initialSearchParams, showToolbar, showScopeFilters, showDownload, showTrackerColumns, columnSettingsTableId}) => {
  const [routeSearchParams, setRouteSearchParams] = useSearchParams();
  const [embeddedSearchParams, setEmbeddedSearchParamsState] = useState(
    () => new URLSearchParams(initialSearchParams)
  );
  const setEmbeddedSearchParams = useCallback((next) => {
    setEmbeddedSearchParamsState((prev) => {
      const mutablePrev = new URLSearchParams(prev);
      const value = typeof next === 'function' ? next(mutablePrev) : next;
      return new URLSearchParams(value);
    });
  }, []);
  const searchParams = embedded ? embeddedSearchParams : routeSearchParams;
  const setSearchParams = embedded ? setEmbeddedSearchParams : setRouteSearchParams;
  const reportGridApiRef = useGridApiRef();
  const session = useSession((state) => state.session);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState(null);
  const downloadingRef = useRef(false);
  const [filter, setFilter] = useState(() => {
    const defaultDateRange = getDefaultDateRange();
    const nextFilter = Object.fromEntries(Object.keys(defaultFilter).map(k => {
      let v;
      if (k.endsWith('_date')) {
        v = searchParams.get(k.slice(0, -5));
        v = v ? dayjs(v) : defaultDateRange[k];
      } else {
        v = searchParams.get(k) || defaultFilter[k];
      }
      return [k, v];
    }));
    if (nextFilter.id) {
      if (nextFilter.idtype === 'id') {
        nextFilter.jobId = nextFilter.id;
      } else {
        nextFilter.orderId = nextFilter.id;
      }
    } else {
      nextFilter.orderId = searchParams.get('order_name') || '';
    }
    return nextFilter;
  });

  const dim = reportType;
  const region = reportType === 'jobregion';
  const reportTitle = region ? '地域报表' : '日报表';
  const reportFormat = region ? 'region' : getReportFormat(searchParams);
  const page = searchParams.get('page')|0;
  const limit = defaultLimit;

  useEffect(() => {
    if (!searchParams.has('limit')) return;
    setSearchParams((prev) => {
      prev.delete('limit');
      return prev;
    });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const params = buildReportParams(searchParams, {region});
    const requestKey = `${dim}?${params.toString()}`;
    let active = true;
    setLoading(true);
    setError('');
    callApi(`/reports/${requestKey}`)
      .then((res) => {
        if (active) {
          setData(res);
        }
      })
      .catch((ex) => {
        if (active) {
          setData(null);
          setError(ex.message || '报表加载失败，请重试');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => { active = false; };
  }, [dim, region, refreshKey, searchParams]);

  const handleFilter = useCallback((ev) => setFilter(prev => ({...prev, [ev.target.name]: ev.target.value})), []);
  const handleOrderIdFilter = useCallback((ev) => {
    const orderId = ev.target.value;
    setFilter(prev => ({
      ...prev,
      idtype: orderId ? 'parent' : (prev.jobId ? 'id' : 'parent'),
      id: orderId || prev.jobId || '',
      orderId,
      jobId: orderId ? '' : prev.jobId,
    }));
  }, []);
  const handleJobIdFilter = useCallback((ev) => {
    const jobId = ev.target.value;
    setFilter(prev => ({
      ...prev,
      idtype: jobId ? 'id' : 'parent',
      id: jobId || prev.orderId || '',
      jobId,
    }));
  }, []);
  const changeDate = (name, val) => setFilter(prev => ({...prev, [name]: val}));
  const submitFilter = (ev) => {
    ev?.preventDefault();
    const prev = new URLSearchParams(searchParams);
    const hasPagination = prev.has('page') || prev.has('limit');
    prev.delete('page');
    prev.delete('limit');

    const sp = new URLSearchParams();
    if (!region) sp.set('format', reportFormat);
    const jobId = filter.jobId.trim();
    const orderQuery = filter.orderId.trim();
    if (jobId) {
      sp.set('idtype', 'id');
      sp.set('id', jobId);
      orderQuery && sp.set('orderId', orderQuery);
    } else if (orderQuery) {
      if (/^\d+$/.test(orderQuery) && Number(orderQuery) > 0) {
        sp.set('idtype', 'parent');
        sp.set('id', orderQuery);
      } else {
        sp.set('order_name', orderQuery);
      }
    }
    filter.q.trim() && sp.set('q', filter.q.trim());
    filter.start_date && sp.set('start', filter.start_date.format('YYYY-MM-DD'));
    filter.end_date && sp.set('end', filter.end_date.format('YYYY-MM-DD'));

    region ? prev.delete('format') : prev.set('format', reportFormat);
    prev.sort();
    const comparable = new URLSearchParams(sp);
    comparable.sort();
    if (hasPagination || prev.toString() !== comparable.toString()) {
      setSearchParams(sp);
    } else {
      setRefreshKey((key) => key + 1);
    }
  };
  const resetFilter = () => setFilter((prev) => {
    const nextFilter = getDefaultFilter();
    if (showScopeFilters) return nextFilter;

    return {
      ...nextFilter,
      idtype: prev.idtype,
      id: prev.id,
      orderId: prev.orderId,
      jobId: prev.jobId,
    };
  });

  const handlePagination = (k, v) => {
    setSearchParams((prev) => {
      if (k === 'page') {
        v > 0 ? prev.set(k, v) : prev.delete(k);
      } else {
        v !== defaultLimit ? prev.set(k, v) : prev.delete(k);
        prev.delete('page'); // return from first page if change limit
      }
      return prev;
    });
  };
  const changeReportFormat = (_, format) => {
    if (!format || format === reportFormat) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('page');
      params.delete('limit');
      params.set('format', format);
      return params;
    });
  };
  const exportReport = async () => {
    if (downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(true);
    const params = buildReportParams(searchParams, {pagination: false, region});
    try {
      const basename = await getReportDownloadBaseName(params, reportTitle);
      const downloadBasename = reportFormat === 'split' ? `${basename}-曝光点击拆分格式` : basename;
      await download(`/reports/export?type=${dim}&${params.toString()}`, downloadBasename);
    } finally {
      downloadingRef.current = false;
      setDownloading(false);
    }
  };
  const columnSettingsButtonId = `job-report-${dim}-column-settings-button`;
  const columnSettingsPanelId = `job-report-${dim}-column-settings-panel`;

  const title = data?.title || reportTitle;
  const displayedData = useMemo(() => {
    if (region) return data;
    if (!data || data.format !== reportFormat) return null;
    if (!showTrackerColumns || reportFormat === 'split') return data;
    const dataColumns = data.columns || [];
    const existingColumns = new Set(dataColumns);
    return {
      ...data,
      columns: [...dataColumns, ...trackerColumnNames.filter(name => !existingColumns.has(name))],
    };
  }, [data, region, showTrackerColumns, reportFormat]);
  const reportContent = (
      <Stack spacing={1} sx={{height: '100%', width: '100%', minHeight: 0, p: embedded ? 0 : 2, pt: embedded ? 0 : 1}}>
        {showToolbar && (
        <Box component="form" onSubmit={submitFilter} sx={{flexShrink: 0}}>
          <Grid container spacing={1} alignItems="center" sx={filterToolbarSx}>
            {showScopeFilters && (
              <>
                <Grid item sx={textFilterItemSx}>
                  <Typography sx={{mr: 0.5, flexShrink: 0}}>订单:</Typography>
                  <OrderIdAutocomplete
                    sx={filterControlSx}
                    name="id"
                    value={filter.orderId}
                    onChange={handleOrderIdFilter}
                    placeholder="ID / 订单名称"
                  />
                </Grid>
                <Grid item sx={textFilterItemSx}>
                  <Typography sx={{mr: 0.5, flexShrink: 0}}>任务:</Typography>
                  <JobIdAutocomplete
                    groupId={filter.orderId}
                    sx={filterControlSx}
                    name="id"
                    value={filter.jobId}
                    onChange={handleJobIdFilter}
                  />
                </Grid>
                <Grid item sx={textFilterItemSx}>
                  <Typography sx={{mr: 0.5, flexShrink: 0}}>名称:</Typography>
                  <JobNameAutocomplete
                    sx={filterControlSx}
                    name="q"
                    value={filter.q}
                    onChange={handleFilter}
                  />
                </Grid>
              </>
            )}
            <Grid item sx={dateFilterItemSx}>
              <Typography sx={{mr: 0.5, flexShrink: 0}}>日期:</Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0}}>
                <DatePicker
                  sx={datePickerSx}
                  value={filter.start_date}
                  views={['year', 'month', 'day']}
                  onChange={val => changeDate('start_date', val)}
                  dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                />
                <Typography component="span" sx={{flexShrink: 0}}>~</Typography>
                <DatePicker
                  sx={datePickerSx}
                  value={filter.end_date}
                  views={['year', 'month', 'day']}
                  minDate={filter.start_date}
                  onChange={val => changeDate('end_date', val)}
                  dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                />
              </Box>
            </Grid>
            <Grid item sx={filterActionsSx}>
              <Button
                type="submit"
                sx={filterButtonSx}
                variant="outlined"
                size="small"
              >搜索</Button>
              <Button
                type="button"
                sx={filterButtonSx}
                variant="outlined"
                size="small"
                onClick={resetFilter}
              >重置</Button>
              {showDownload && (
                  <Button
                    type="button"
                    sx={filterButtonSx}
                    variant="outlined"
                    size="small"
                    startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                    disabled={loading || downloading || Boolean(error)}
                    onClick={exportReport}
                  >{downloading ? '下载中' : '下载'}</Button>
              )}
              <AppDataGridColumnSettingsButton
                apiRef={reportGridApiRef}
                buttonId={columnSettingsButtonId}
                panelId={columnSettingsPanelId}
                label={t('Column Settings')}
                ref={setColumnSettingsAnchorEl}
                type="button"
              />
            </Grid>
          </Grid>
        </Box>
        )}
        {(!region || loading) && <Stack direction="row" spacing={1.5} alignItems="center" sx={{flexShrink: 0}}>
          {!region && (
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={reportFormat}
            onChange={changeReportFormat}
            aria-label="报表视图"
            sx={{flexShrink: 0}}
          >
            <ToggleButton value="split">展点视图</ToggleButton>
            <ToggleButton value="detail">明细视图</ToggleButton>
          </ToggleButtonGroup>
          )}
          {loading && <Box role="status" sx={{display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary'}}>
            <CircularProgress size={16} color="inherit" />
            <Typography variant="body2">正在加载{region ? reportTitle : reportFormat === 'split' ? '展点视图' : '明细视图'}…</Typography>
          </Box>}
        </Stack>}
        {error && <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => setRefreshKey(key => key + 1)}>重试</Button>
        }>{error}</Alert>}
        <ReportTable
          key={reportFormat}
          apiRef={reportGridApiRef}
          data={displayedData}
          columns={columns}
          getColumnLabel={(col) => displayedData?.columnLabels?.[col] || columns[col]?.label || formatFieldName(col)}
          renderCell={renderCellValue}
          getCellTitle={(col, row) => row[col]}
          getRowKey={(row) => row.id}
          page={page}
          rowsPerPage={limit}
          onPagination={handlePagination}
          showColumnSettings={showToolbar}
          columnSettingsTableId={`${columnSettingsTableId || `report-${dim}`}${reportFormat === 'split' ? '-split' : ''}`}
          columnSettingsUserId={session?.uid}
          columnSettingsAnchorEl={columnSettingsAnchorEl}
          loading={loading}
        />
      </Stack>
  );

  return embedded ? reportContent : (
    <Page title={title} styles={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      {reportContent}
    </Page>
  );
};

JobReports.propTypes = {
  reportType: PropTypes.oneOf(['jobdaily', 'jobregion']),
  embedded: PropTypes.bool,
  initialSearchParams: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(URLSearchParams),
  ]),
  showToolbar: PropTypes.bool,
  showScopeFilters: PropTypes.bool,
  showDownload: PropTypes.bool,
  showTrackerColumns: PropTypes.bool,
  columnSettingsTableId: PropTypes.string,
};

JobReports.defaultProps = {
  reportType: 'jobdaily',
  embedded: false,
  initialSearchParams: '',
  showToolbar: true,
  showScopeFilters: true,
  showDownload: true,
  showTrackerColumns: false,
  columnSettingsTableId: null,
};

export default JobReports;
